"use client";

import { useState, useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import { m } from "motion/react";
import { RemoveScroll } from "react-remove-scroll";
import { Icon } from "@/components/icon";
import type { NavLink } from "./header/header.interface";
import { useBodyScrollClass, useNavEscapeClose } from "./use-navigation";
import { useLogoScrollAnimation } from "./use-logo-scroll-animation";
import {
  hamburgerBottom,
  hamburgerMiddle,
  hamburgerTop,
  linkItemVariants,
  linkListVariants,
  panelVariants,
} from "./navigation.animation";
import messages from "@/messages/en.json";
import { usePathname } from "next/navigation";

const opensInNewTab = messages.common.opensInNewTab;

interface MobileNavProps {
  siteName?: string;
  nav?: (NavLink & { id?: string })[];
  facebookUrl?: string;
  instagramUrl?: string;
  facebookLabel: string;
  instagramLabel: string;
}

export function Navigation({
  siteName,
  nav,
  facebookUrl,
  instagramUrl,
  facebookLabel,
  instagramLabel,
}: MobileNavProps) {
  const [isOpen, setIsOpen] = useState(false);
  const hasSocial = facebookUrl || instagramUrl;
  const hasNav = nav && nav.length > 0;
  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const logoRef = useRef<HTMLImageElement>(null);
  const logoCircleRef = useRef<HTMLAnchorElement>(null);

  const isHomepage = usePathname() === "/";

  useLogoScrollAnimation(logoRef, logoCircleRef);

  const handleClose = () => {
    if (isHomepage) {
      return;
    }
    setIsOpen(false);
  };

  const handleHamburgerClick = () => {
    setIsOpen(!isOpen);
  };

  useBodyScrollClass();
  useNavEscapeClose(isOpen, () => setIsOpen(false), hamburgerRef);

  const animateState = isOpen ? "open" : "closed";

  return (
    <div className="bg-yeast xl:px-page-x relative">
      <div>
        <div className="px-page-x-mobile h-section grid grid-cols-[1fr_auto_1fr] pt-1 xl:flex xl:h-24 xl:items-center xl:gap-9 xl:px-0 xl:py-0">
          <button
            ref={hamburgerRef}
            type="button"
            onClick={handleHamburgerClick}
            className="border-hop text-hop mt-3.5 flex h-11.5 w-11.5 items-center justify-center justify-self-start rounded-[6px] border xl:hidden"
            aria-label={
              isOpen ? "Close navigation menu" : "Open navigation menu"
            }
            aria-expanded={isOpen}
            aria-controls="mobile-nav-panel"
            aria-haspopup="true"
          >
            {/* Three lines that morph into an X.
                All three sit centred in the container; their `y` offset
                positions them when closed, and they collapse to centre
                (y=0) with a ±45° rotate when open. */}
            <span
              className="relative flex h-5 w-5.5 items-center justify-center"
              aria-hidden="true"
            >
              <m.span
                className="bg-hop absolute h-0.5 w-5.5 origin-center"
                variants={hamburgerTop}
                animate={animateState}
                initial={false}
              />
              <m.span
                className="bg-hop absolute h-0.5 w-5.5 origin-center"
                variants={hamburgerMiddle}
                animate={animateState}
                initial={false}
              />
              <m.span
                className="bg-hop absolute h-0.5 w-5.5 origin-center"
                variants={hamburgerBottom}
                animate={animateState}
                initial={false}
              />
            </span>
          </button>

          {/* Logo — centred on mobile, self-start on desktop so it overflows below the 50px bar */}
          <div className="flex justify-center self-start xl:flex-none xl:justify-start">
            <Link
              ref={logoCircleRef}
              href="/"
              aria-label={siteName ?? "The Crafty Pint"}
              className="nav-logo-circle bg-yeast flex items-center justify-center rounded-full xl:size-auto xl:rounded-[84px] xl:border-0"
              onClick={handleClose}
            >
              <Image
                ref={logoRef}
                src="/icons/logo.svg"
                alt=""
                width={168}
                height={168}
                className="nav-logo xl:mt-9"
              />
            </Link>
          </div>

          {/* Desktop spacer */}
          <div className="hidden flex-1 xl:block" />

          {/* Desktop nav */}
          {hasNav && (
            <nav className="hidden xl:block">
              <ul className="m-0 flex list-none items-center gap-7 p-0 whitespace-nowrap 2xl:gap-9">
                {nav.map((link) => (
                  <li key={link.id}>
                    <Link
                      href={link.href}
                      className="text-hop hover:text-copper-kettle text-base leading-[22px] font-medium transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          )}

          {/* Desktop divider */}
          {hasSocial && hasNav && (
            <div className="bg-hop hidden h-7 w-px xl:block" />
          )}

          {/* Social icons — same element on both breakpoints */}
          {hasSocial && (
            <div
              className="mt-3.5 flex justify-end gap-4.5 md:gap-3 xl:mt-0"
              inert={isOpen || undefined}
            >
              {facebookUrl && (
                <a
                  href={facebookUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="border-hop text-hop flex size-[22px] items-center justify-center rounded border transition-opacity hover:opacity-70"
                  aria-label={`${facebookLabel} ${opensInNewTab}`}
                >
                  <Icon name="facebook" className="h-3.5 w-2" />
                </a>
              )}
              {instagramUrl && (
                <a
                  href={instagramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="border-hop text-hop mr-0.75 flex size-[22px] items-center justify-center rounded border transition-opacity hover:opacity-70 md:mr-0"
                  aria-label={`${instagramLabel} ${opensInNewTab}`}
                >
                  <Icon name="instagram" className="h-3.5 w-3.5" />
                </a>
              )}
            </div>
          )}
        </div>
      </div>

      {hasNav && (
        <RemoveScroll enabled={isOpen} forwardProps removeScrollBar={false}>
          <m.div
            id="mobile-nav-panel"
            className="border-malt bg-blush-lambic fixed inset-0 z-[-1] border-t pt-[200px] xl:hidden"
            variants={panelVariants}
            initial={false}
            animate={animateState}
            aria-hidden={!isOpen}
            inert={!isOpen}
          >
            <nav>
              <m.ul
                className="border-hop mx-page-x-mobile m-0 list-none border-t p-0"
                variants={linkListVariants}
                initial={false}
                animate={animateState}
              >
                {nav.map((link) => (
                  <m.li
                    key={link.id}
                    className="border-hop border-b"
                    variants={linkItemVariants}
                  >
                    <Link
                      href={link.href}
                      onClick={() => setIsOpen(false)}
                      className="font-accent text-copper-kettle text-heading-lg block py-3 transition-colors active:text-hop hover:opacity-70"
                    >
                      {link.label}
                    </Link>
                  </m.li>
                ))}
              </m.ul>
            </nav>
          </m.div>
        </RemoveScroll>
      )}
    </div>
  );
}
